import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthModal() {
  const { authModal, closeAuthModal, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(name, email, password);
      }
      toast.success(mode === "login" ? "Welcome back to the Lab." : "Account created — Theo unlocks the vault after you pay in person.");
      closeAuthModal();
      setName(""); setEmail(""); setPassword("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/plans";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AnimatePresence>
      {authModal.open && (
        <motion.div
          data-testid="auth-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={closeAuthModal}
        >
          <motion.div
            className="relative w-full max-w-md bg-court-surface border border-white/10 rounded-2xl p-8 shadow-2xl"
            initial={{ y: 40, opacity: 0, scale: 0.96 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <button data-testid="auth-modal-close" onClick={closeAuthModal} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors">
              <X size={20} />
            </button>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-court-amber/15 flex items-center justify-center">
                <Lock size={18} className="text-court-amber" />
              </div>
              <h3 className="font-display font-extrabold uppercase text-2xl tracking-tight">Member Access</h3>
            </div>
            <p className="text-sm text-zinc-400 mb-6">{authModal.message || "Log in or create a free account — Theo unlocks the full blueprint vault after you pay in person."}</p>

            <button data-testid="auth-google-btn" type="button" onClick={signInWithGoogle}
              className="w-full mb-5 flex items-center justify-center gap-3 bg-white text-zinc-900 font-semibold text-sm py-3 rounded-lg hover:bg-zinc-200 transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.02.15 3.5 2.7.24.02c2.2-2 3.5-5 3.5-8.6z"/><path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.8-5H1.1v3c2 4 6.2 6.6 10.9 6.6z"/><path fill="#FBBC05" d="M5.2 14.4c-.25-.7-.4-1.5-.4-2.4s.15-1.7.4-2.4v-3H1.1C.4 8.3 0 10.1 0 12s.4 3.7 1.1 5.4l4.1-3z"/><path fill="#EA4335" d="M12 4.6c1.8 0 3 .8 3.7 1.4l2.7-2.6C16.9 1.2 15.2 0 12 0 7.3 0 3.1 2.6 1.1 6.6l4.1 3c1-2.9 3.7-5 6.8-5z"/></svg>
              Continue with Google
            </button>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px flex-1 bg-white/10" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">or with email</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <div className="flex gap-2 mb-6">
              <button data-testid="auth-tab-login" onClick={() => { setMode("login"); setError(""); }}
                className={`flex-1 py-2 rounded-lg font-mono text-xs uppercase tracking-widest transition-colors ${mode === "login" ? "bg-court-amber text-court-void font-bold" : "bg-white/5 text-zinc-400 hover:text-white"}`}>
                Log In
              </button>
              <button data-testid="auth-tab-register" onClick={() => { setMode("register"); setError(""); }}
                className={`flex-1 py-2 rounded-lg font-mono text-xs uppercase tracking-widest transition-colors ${mode === "register" ? "bg-court-amber text-court-void font-bold" : "bg-white/5 text-zinc-400 hover:text-white"}`}>
                Sign Up
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <input data-testid="auth-name-input" className="input-dark" placeholder="Parent / guardian name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
              )}
              <input data-testid="auth-email-input" type="email" className="input-dark" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <input data-testid="auth-password-input" type="password" className="input-dark" placeholder="Password (min 6 characters)" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
              {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
              <button data-testid="auth-login-btn" type="submit" disabled={busy}
                className="btn-amber w-full bg-court-amber text-court-void font-display font-extrabold uppercase tracking-wide text-lg py-3 rounded-lg disabled:opacity-60">
                {busy ? "Checking..." : mode === "login" ? "Log In & Unlock" : "Create Account"}
              </button>
            </form>
            <p className="mt-4 text-xs font-mono text-zinc-500 uppercase tracking-wider">Demo account: demo@theobasketballlab.com</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
